/**
 * Browse view controller. Each capsule triggers its own server-side
 * API request — no pre-loading of unrelated data. This ensures
 * paginated responses stay accurate and reduces unnecessary API load.
 */
import type { Root } from "./prototypeTypes";
import type { PrototypeRuntime } from "./prototypeTypes";
import { byId } from "./domUtils";
import { escapeHtml } from "./formatUtils";
import { buildTopicCard } from "./topicCard";
import { clearListEnterAnimations, clearPagedInfiniteList, invalidatePagedInfiniteList, renderPagedInfiniteList, suspendPagedInfiniteList } from "./progressiveList";
import { applyVisitorCatalogCta } from './visitorCatalogCta';
import { DEFAULT_BROWSE_SORT, loadBrowsePage } from "./browseData";
import type { BrowseSort } from './browseData';
import type { BrowseLoadError, BrowsePage, PublicTopic } from "./browseTypes";
import { renderEmptyStateMarkup, type EmptyStateIcon } from './emptyStateMarkup';
import { browseViewCacheKey, readBrowseViewCache, writeBrowseViewCache } from './topicBrowseViewCache';
import { deleteBrowseViewCache } from './topicBrowseViewCache';
import {
	activateBrowsePaginationSession,
	browsePaginationScopeKey,
	consumeBrowseReturnSessionFromHistory,
	createBrowseHistoryEntryState,
	createBrowsePaginationSession,
	discardActiveBrowsePaginationSession,
	hasBrowseReturnSessionMarker,
	recordBrowseHistorySnapshot,
	syncCurrentBrowseHistoryEntry,
	updateBrowsePaginationSession,
} from './browsePaginationSessionStore';
import { showToast } from './toastController';
import { closeBrowseIndustryMenu, syncBrowseIndustryMenu, wireBrowseIndustryMenu } from './browseIndustryMenu';
import { finishMobileBottomDockHistoryRestore, mobileBottomDockHistorySnapshot, persistMobileBottomDockHistory } from './mobileBottomDockController';
import { shouldCommitTextInputOnEnter } from './inputComposition';
import { browseSeoTopics, readBrowseSeoPageModel } from './browseSeoPageModel';
import { resetSpaDocumentSeo } from './spaDocumentSeo';

const BROWSE_EMPTY_MESSAGES: Record<string, { title: string; subtitle: string; icon: EmptyStateIcon }> = {
  recent: {
    title: "No recent topics",
    subtitle: "Topics you've viewed will appear here",
    icon: 'clock',
  },
  recommended: {
    title: "No recommendations yet",
    subtitle: "Personalized picks will appear as you explore",
    icon: 'userRoundCheck',
  },
  favorites: {
    title: "No saved topics yet",
    subtitle: "Save any topic to find it here later",
    icon: 'bookmark',
  },
  all: {
    title: "No topics yet",
    subtitle: "New topics will appear here as they are published",
    icon: 'book',
  },
  newest: {
    title: 'Nothing new yet',
    subtitle: 'Fresh topics will appear here as they’re published.',
    icon: 'sparkles',
  },
  trending: {
    title: 'No Trending topics yet',
    subtitle: 'Topics will appear here as they gain momentum.',
    icon: 'flame',
  },
  // Bug 2 fix: category-filtered empty state. Previously, when a
  // category with 0 topics (e.g. education-learning) was selected,
  // the code fell through to BROWSE_EMPTY_MESSAGES.all — "No topics
  // yet" — which misled users into thinking the entire platform had
  // no content. This entry is the fallback for ANY category slug
  // that isn't one of the 4 named capsules above.
  category: {
    title: "No topics in this category yet",
    subtitle: "Try another category, or explore All to see everything",
    icon: 'book',
  },
};

/**
 * Optional one-shot capsule override. Set by activateBrowseFilter()
 * when the user clicks a "See all <X>" link on the home page that
 * should land on a specific capsule (favorites / recent) rather than
 * the default. refreshBrowseGrid consumes and clears it on the next
 * run, so the override only applies to the immediate navigation —
 * subsequent Browse-menu clicks still reset to the default.
 *
 * This lets us satisfy both requirements:
 *   - "Browse menu click → always land on All"
 *   - "Home 'See all favorites' → land on Favorites"
 */
type PendingBrowseIntent = { filter: string; sort?: BrowseSort };

let pendingBrowseIntent: PendingBrowseIntent | null = null;

export function setPendingBrowseFilter(filter: string | null): void {
	pendingBrowseIntent = filter === null ? null : { filter };
}

/** Sets the one-shot Home-to-Browse intent so filter and ranking land together. */
export function setPendingBrowseIntent(filter: string, sort?: BrowseSort): void {
	pendingBrowseIntent = { filter, sort };
}

/** Ends the one-popstate return guard when Browse is left for another view. */
export function clearBrowseReturnRestoreGuard(root: Root): void {
	const holder = root as unknown as { __browseReturnRestoreScope?: string; __browseReturnRestoreGeneration?: number };
	delete holder.__browseReturnRestoreScope;
	delete holder.__browseReturnRestoreGeneration;
}

/** Freezes a Browse page-two request before a real Browse-card → Topic return. */
export function suspendBrowsePaginationForReturn(root: Root): void {
	const grid = byId(root, 'browseGrid');
	if (!grid || grid.querySelector('.topic-card') === null) return;
	suspendPagedInfiniteList(grid);
}

/**
 * Cancels ownership of every in-flight Browse result without touching retained
 * cards. A Topic return can re-use that DOM, so a request started before the
 * departure must never become current again merely because the same filter,
 * scope and view are active after Back.
 */
export function invalidateBrowseRequests(root: Root, options: { preserveReturnReserve?: boolean } = {}): void {
	const holder = root as unknown as { __browseRequestGeneration?: number; __browseNavigationEpoch?: number };
	holder.__browseNavigationEpoch = (holder.__browseNavigationEpoch ?? 0) + 1;
	holder.__browseRequestGeneration = (holder.__browseRequestGeneration ?? 0) + 1;
	const grid = byId(root, 'browseGrid');
	if (grid) invalidatePagedInfiniteList(grid, options);
}

/**
 * Paint the route's Browse intent before session hydration begins. This keeps
 * direct `?sort=trending` loads truthful without starting a data request.
 */
export function syncInitialBrowseRouteIntent(root: Root): void {
	const seoPage = readBrowseSeoPageModel();
	if (seoPage) {
		const holder = root as unknown as { __browseFilter?: string; __browseSort?: BrowseSort };
		setBrowseSearch(root, '');
		holder.__browseFilter = 'all';
		holder.__browseSort = DEFAULT_BROWSE_SORT;
		syncBrowseSortControls(root, 'all', DEFAULT_BROWSE_SORT);
		return;
	}
	const intent = readBrowseUrlIntent();
	const filter = intent.filter ?? 'all';
	const sort = canonicalBrowseSort(intent.sort);
	const search = intent.search;
	const holder = root as unknown as { __browseFilter?: string; __browseSort?: BrowseSort };
	setBrowseSearch(root, search);
	canonicalizeBrowseUrl(filter, sort, search);
	holder.__browseFilter = filter;
	holder.__browseSort = sort;
	syncBrowseSortControls(root, filter, sort);
}

/**
 * Load the browse capsule and render it. Called every time the Browse
 * view becomes active. Resolution order for the capsule:
 *   1. pendingBrowseFilter (one-shot override from home See-all links)
 *   2. default ("all" for every viewer)
 * It NEVER inherits a previously-selected capsule from a prior visit.
 *
 * The only guard is a global per-view request dedupe (shouldHydrate)
 * so the duplicate showView calls from controller init + hydration
 * don't fire two requests.
 */
export async function refreshBrowseGrid(
  root: Root,
  runtime: PrototypeRuntime,
  options: { preserveActiveFilter?: boolean } = {},
): Promise<void> {
  const grid = byId(root, "browseGrid");
  if (!grid) return;
	const wrapper = grid.parentElement;

	const holder = root as unknown as {
    __browseFilter?: string;
    __browseRuntime?: PrototypeRuntime;
    __browseFetchedFilter?: string;
		__browseSort?: BrowseSort;
		__browseReturnRestoreScope?: string;
		__browseReturnRestoreGeneration?: number;
  };

  // Consume the one-shot override FIRST, before the dedupe gate. If we
  // gated first, a near-duplicate showView call could swallow the
  // override without rendering it, leaking it to the next visit.
	const pendingIntent = pendingBrowseIntent;
	const hasOverride = pendingIntent !== null;
	const seoPage = hasOverride ? null : readBrowseSeoPageModel();
	if (seoPage) {
		pendingBrowseIntent = null;
		// This fixed server page replaces the ordinary stream. Invalidate its
		// ownership first so a late all/newest response cannot repaint this page.
		invalidateBrowseRequests(root);
		holder.__browseRuntime = runtime;
		holder.__browseFilter = 'all';
		holder.__browseSort = DEFAULT_BROWSE_SORT;
		holder.__browseFetchedFilter = 'all';
		setBrowseSearch(root, '');
		wireBrowseSearch(root);
		wireBrowseHistoryScroll(root);
		wireBrowseIndustryMenu(root);
		highlightActivePill(root, 'all');
		syncBrowseIndustryMenu(root, 'all');
		syncBrowseSortControls(root, 'all', DEFAULT_BROWSE_SORT);
		renderBrowseSeoPage(root, browseSeoTopics(seoPage));
		if (wrapper) wrapper.setAttribute('data-state', 'loaded');
		return;
	}
	const urlIntent = readBrowseUrlIntent();
	const restoreStoredIntent = options.preserveActiveFilter && !urlIntent.hasExplicitIntent;
	const filter = pendingIntent?.filter ?? (restoreStoredIntent ? holder.__browseFilter : urlIntent.filter) ?? 'all';
	const requestedSort = pendingIntent?.sort ?? (restoreStoredIntent ? holder.__browseSort : urlIntent.sort);
	const sort = canonicalBrowseSort(requestedSort);
	const search = pendingIntent || restoreStoredIntent || (options.preserveActiveFilter && !urlIntent.hasSearchParam)
		? currentBrowseSearch(root)
		: urlIntent.search;
	pendingBrowseIntent = null;
	setBrowseSearch(root, search);
	const canonicalizedHistorySnapshot = canonicalizeBrowseUrl(filter, sort, search);

  // Persist runtime FIRST so capsule-switch handlers (applyBrowseGridFilter)
  // always have a valid runtime ref, even when the dedupe gate trips below.
  holder.__browseRuntime = runtime;

	holder.__browseFilter = filter;
	holder.__browseSort = sort;
  holder.__browseFetchedFilter = undefined;
	if (canonicalizedHistorySnapshot) syncCurrentBrowseHistoryEntry(root, runtime, filter, search, sort);

	wireBrowseSearch(root);
	wireBrowseHistoryScroll(root);
	wireBrowseIndustryMenu(root);
  highlightActivePill(root, filter);
	syncBrowseIndustryMenu(root, filter);
	syncBrowseSortControls(root, filter, sort);
	const scopeKey = browsePaginationScopeKey(runtime, filter, search, sort);
	const hadBrowseReturnSessionMarker = hasBrowseReturnSessionMarker(window.history.state);
	const returnSession = consumeBrowseReturnSessionFromHistory(scopeKey);
	if (returnSession) {
		// The preserved pagination session is a new ownership boundary. Invalidate
		// any request that was started before Topic departure before it can observe
		// the now-active Browse view and remount its stale first page.
		invalidateBrowseRequests(root, { preserveReturnReserve: true });
		holder.__browseReturnRestoreScope = scopeKey;
		holder.__browseReturnRestoreGeneration = (holder.__browseReturnRestoreGeneration ?? 0) + 1;
		activateBrowsePaginationSession(root, returnSession.id);
		// The marker was atomically consumed above. Keep the route snapshot, but
		// never serialize another Topic-return marker during this restoration.
		syncCurrentBrowseHistoryEntry(root, runtime, filter, search, sort, returnSession.scrollY);
		clearListEnterAnimations(grid);
		mountBrowsePagination(root, runtime, filter, search, sort, returnSession.topics, returnSession.nextCursor, returnSession.hasMore, returnSession.rankingVersion, scopeKey, true);
		if (wrapper) wrapper.setAttribute('data-state', 'loaded');
		commitBrowseReturnViewport(root, grid, returnSession.scrollY);
		return;
	}
	if (hadBrowseReturnSessionMarker) {
		commitBrowseReturnViewport(root, grid, 0);
	}
	// A duplicate showView/hydrate after this same trusted Back sees the
	// consumed marker. Its retained pager is already registered; fetching page
	// one here would replace the cards we just restored.
	if (holder.__browseReturnRestoreScope === scopeKey) return;

  // Global dedupe: showView() can fire multiple times during a single
  // navigation (controller init -> hydratePrototypeRuntime), so gate
  // the request on a short time window per view key. A trusted Topic return
  // is consumed above and must never be dropped by this duplicate guard.
  if (!hasOverride) {
    const { shouldHydrate } = await import("./hydrateGuard");
    if (!shouldHydrate("browse")) return;
  }
	await fetchAndRenderBrowse(root, runtime, filter, search, sort);
}

/**
 * A retained Browse pager must be measurable before the popstate handler ends.
 * Deferring this scroll to rAF exposed the active Browse view at its old (zero)
 * viewport for one or two frames. Keep the temporary layout override narrowly
 * scoped to this return, synchronously flush the retained grid, then commit and
 * rebase the Dock in the same task.
 */
function commitBrowseReturnViewport(root: Root, grid: HTMLElement, scrollY: number): void {
	const view = grid.closest<HTMLElement>('#view-home-browse');
	view?.classList.add('browse-return-committing');
	try {
		// These reads intentionally force the retained cards and their reserve
		// geometry to participate in layout before scrollTo can be clamped.
		view?.getBoundingClientRect();
		grid.getBoundingClientRect();
		void document.documentElement.scrollHeight;
		window.scrollTo({ top: Math.max(0, scrollY), behavior: 'instant' as ScrollBehavior });
		// Read the committed position before releasing the no-transition Dock
		// guard; finishHistoryRestore rebases from this exact viewport.
		void window.scrollY;
		finishMobileBottomDockHistoryRestore(root);
	} finally {
		view?.classList.remove('browse-return-committing');
	}
}

/**
 * Read the current browse search input value. Centralized so every
 * entry point (initial render, capsule switch, search commit, clear)
 * agrees on what "current search" means.
 */
function currentBrowseSearch(root: Root): string {
  const input = byId(root, "browseSearchInput") as HTMLInputElement | null;
  return normalizeBrowseSearch(input?.value);
}

function normalizeBrowseSearch(value: unknown): string {
	return typeof value === 'string' ? value.trim().slice(0, 120) : '';
}

function setBrowseSearch(root: Root, search: string): void {
	const input = byId(root, 'browseSearchInput') as HTMLInputElement | null;
	if (input) input.value = search;
	toggleBrowseClearButton(root);
}

/**
 * Fetch topics for a specific capsule from the server and render.
 * This is the single path for all capsule clicks and all search
 * commits — search is forwarded to the server so pagination stays
 * accurate across the full result set, not just the cached page.
 */
async function fetchAndRenderBrowse(root: Root, runtime: PrototypeRuntime, filter: string, search: string, sort: BrowseSort = DEFAULT_BROWSE_SORT): Promise<void> {
  const grid = byId(root, "browseGrid");
	const wrapper = grid?.parentElement;
	if (!grid) return;
	// Sort, filter, and search actions have already updated history by this
	// point. Reconcile the head with that ordinary Browse variant before its
	// request resolves, so page-specific metadata cannot leak out of SEO mode.
	resetSpaDocumentSeo(window.location.pathname);
	clearBrowseSeoPagination(root);
	const empty = byId(root, "browseEmpty");
	// A previous empty state hides the grid wrapper (see showBrowseEmpty) to
	// remove the collapsed grid's bottom margin. Restore it so the loading
	// skeleton and fresh cards render on this new load.
	if (wrapper) wrapper.style.display = "";

  const holder = root as unknown as {
    __browseFilter?: string;
    __browseFetchedFilter?: string;
    __browseRenderedKey?: string;
		__browseRequestGeneration?: number;
		__browseNavigationEpoch?: number;
		__browseSort?: BrowseSort;
	};
	const cacheKey = browseViewCacheKey(runtime, filter, search, sort);
	const scopeKey = browsePaginationScopeKey(runtime, filter, search, sort);
	const cached = readBrowseViewCache(cacheKey);
	const existingCards = grid.querySelectorAll('.topic-card').length;
	if (isSnapshotPaginationSort(sort) && cached && existingCards === 0) {
		grid.replaceChildren(...cached.topics.map((topic) => buildCard(topic)));
		holder.__browseRenderedKey = cacheKey;
		(root as unknown as { __browseTopicsCache?: PublicTopic[] }).__browseTopicsCache = cached.topics;
	}
	const canKeepExistingCards = holder.__browseRenderedKey === cacheKey && grid.querySelectorAll('.topic-card').length > 0;
	const generation = (holder.__browseRequestGeneration ?? 0) + 1;
	holder.__browseRequestGeneration = generation;
	const navigationEpoch = holder.__browseNavigationEpoch ?? 0;
	holder.__browseNavigationEpoch = navigationEpoch;

	// Back navigation must never blank a correct, already-visible grid just to
	// ask the server whether it changed. The request below still always runs;
	// this is a first-paint restore, not a data freshness cache.
	if (canKeepExistingCards) {
		if (wrapper) wrapper.setAttribute('data-state', 'refreshing');
		let response: BrowsePage;
		try {
			response = await loadBrowsePage(runtime, filter, null, search, sort);
		} catch {
			if (!isCurrentBrowseRequest(root, runtime, holder, generation, navigationEpoch, cacheKey, filter, search, sort)) return;
			if (wrapper) wrapper.setAttribute('data-state', 'stale');
			return;
		}
		if (!isCurrentBrowseRequest(root, runtime, holder, generation, navigationEpoch, cacheKey, filter, search, sort)) return;
		holder.__browseFetchedFilter = filter;
		if (response.error) {
			if (wrapper) wrapper.setAttribute('data-state', 'stale');
			return;
		}
		updateBrowseVisitorCatalogCta(root, filter, search, response.totalTopics);
		const rankingVersion = (response as BrowsePage & { rankingVersion?: string }).rankingVersion;
		writeBrowseViewCache(browseViewCacheKey(runtime, filter, search, sort, rankingVersion), response.topics, rankingVersion);
		(root as unknown as { __browseTopicsCache?: PublicTopic[] }).__browseTopicsCache = response.topics;
		if (response.topics.length === 0) {
			grid.replaceChildren();
			showBrowseEmpty(root, filter, search, sort);
		} else {
			mountBrowsePagination(root, runtime, filter, search, sort, response.topics, response.nextCursor, response.hasMore, rankingVersion, scopeKey);
			if (empty) empty.style.display = 'none';
		}
		if (wrapper) wrapper.setAttribute('data-state', 'loaded');
		return;
	}

	if (wrapper) wrapper.setAttribute('data-state', 'loading');

  if (empty) empty.style.display = "none";
  let firstPage = true;
	let retriedExpiredRanking = false;
	let sessionCreated = false;
	let sessionRankingVersion: string | undefined;
	let sessionPageState = { nextCursor: null as string | null, hasMore: false };
	let resolveFirstPage: (() => void) | undefined;
	const firstPageSettled = new Promise<void>((resolve) => { resolveFirstPage = resolve; });
  renderPagedInfiniteList({
    container: grid,
    loadPage: async (cursor) => {
		const isFirstRequest = firstPage;
		try {
		let resp = await loadBrowsePage(runtime, filter, cursor, search, sort);
		if (isSnapshotPaginationSort(sort) && resp.error === 'ranking_expired' && !retriedExpiredRanking) {
			retriedExpiredRanking = true;
			if (cursor) {
				void restartBrowseAfterExpiredRanking(root, runtime, filter, search, sort, cacheKey, { requestGeneration: generation, navigationEpoch });
				return { items: [], hasMore: false, nextCursor: null };
			}
			// A 410 cursor is tied to an expired immutable ranking. Restart from
			// page one once rather than mixing a fresh snapshot into this stream.
			resp = await loadBrowsePage(runtime, filter, null, search, sort);
		}
      if (firstPage) {
        firstPage = false;
			if (!isCurrentBrowseRequest(root, runtime, holder, generation, navigationEpoch, cacheKey, filter, search, sort)) return { items: [], hasMore: false, nextCursor: null };
        holder.__browseFetchedFilter = filter;
			holder.__browseRenderedKey = cacheKey;
		if (resp.error) {
			if (wrapper) wrapper.setAttribute("data-state", "loaded");
			showBrowseError(root, browseUnavailableKind(resp.error));
        } else if (resp.topics.length === 0) {
			if (wrapper) wrapper.setAttribute("data-state", "loaded");
		  showBrowseEmpty(root, filter, search, sort);
		} else {
			const rankingVersion = (resp as BrowsePage & { rankingVersion?: string }).rankingVersion;
			sessionRankingVersion = rankingVersion;
			writeBrowseViewCache(browseViewCacheKey(runtime, filter, search, sort, rankingVersion), resp.topics, rankingVersion);
			if (wrapper) wrapper.setAttribute("data-state", "loaded");
        }
		updateBrowseVisitorCatalogCta(root, filter, search, resp.totalTopics);
      }
      return { items: resp.topics, hasMore: resp.hasMore, nextCursor: resp.nextCursor };
		} finally {
			if (isFirstRequest) resolveFirstPage?.();
		}
    },
    renderItem: (topic) => buildCard(topic),
    skeletonClassName: "topic-card-compact-skeleton",
    skeletonCount: 12,
    endMessage: "You've reached the end of the topic catalog.",
		onItemsChange: (topics) => {
      (root as unknown as { __browseTopicsCache?: PublicTopic[] }).__browseTopicsCache = topics;
			if (sessionCreated) updateBrowsePaginationSession(root, { topics, ...sessionPageState, rankingVersion: sessionRankingVersion });
		if (topics.length > 0 && empty) empty.style.display = "none";
			if (sessionCreated) syncCurrentBrowseHistoryEntry(root, runtime, filter, search, sort);
    },
		onPage: (page) => {
			sessionPageState = { nextCursor: page.nextCursor ?? null, hasMore: Boolean(page.hasMore) };
			if (!sessionCreated && page.items.length > 0) {
				createBrowsePaginationSession(root, { scopeKey, topics: page.items, ...sessionPageState, rankingVersion: sessionRankingVersion });
				sessionCreated = true;
				return;
			}
			if (sessionCreated) updateBrowsePaginationSession(root, {
					topics: (root as unknown as { __browseTopicsCache?: PublicTopic[] }).__browseTopicsCache ?? [],
					nextCursor: page.nextCursor ?? null,
					hasMore: Boolean(page.hasMore),
					rankingVersion: sessionRankingVersion,
				});
				syncCurrentBrowseHistoryEntry(root, runtime, filter, search, sort);
		},
	});
	await firstPageSettled;
}

function renderBrowseSeoPage(root: Root, topics: PublicTopic[]): void {
	const grid = byId(root, 'browseGrid');
	if (!grid) return;
	clearPagedInfiniteList(grid);
	clearBrowseSeoPagination(root);
	grid.replaceChildren(...topics.map((topic) => buildCard(topic)));
	(root as unknown as { __browseTopicsCache?: PublicTopic[] }).__browseTopicsCache = topics;
	byId(root, 'browseEmpty')?.setAttribute('style', 'display:none;');
	const wrapper = grid.parentElement;
	if (wrapper) wrapper.style.display = '';
	const model = readBrowseSeoPageModel();
	if (!model) return;
	const nav = grid.ownerDocument.createElement('nav');
	nav.id = 'browseSeoPagination';
	nav.className = 'browse-seo-pagination';
	nav.setAttribute('aria-label', 'Browse catalog pages');
	if (model.page >= 2) nav.appendChild(browseSeoPageLink(grid.ownerDocument, model.page - 1, 'Previous page', 'prev'));
	const status = grid.ownerDocument.createElement('span');
	status.textContent = `Page ${model.page}${model.lastPage === null ? '' : ` of ${model.lastPage}`}`;
	nav.appendChild(status);
	if (model.lastPage !== null && model.page < model.lastPage) nav.appendChild(browseSeoPageLink(grid.ownerDocument, model.page + 1, 'Next page', 'next'));
	wrapper?.appendChild(nav);
}

function browseSeoPageLink(doc: Document, page: number, label: string, rel: 'prev' | 'next'): HTMLAnchorElement {
	const link = doc.createElement('a');
	link.href = `/browse?page=${page}`;
	link.rel = rel;
	link.textContent = label;
	return link;
}

function clearBrowseSeoPagination(root: Root): void {
	byId(root, 'browseSeoPagination')?.remove();
}

function mountBrowsePagination(
	root: Root,
	runtime: PrototypeRuntime,
	filter: string,
	search: string,
	sort: BrowseSort,
	topics: PublicTopic[],
	nextCursor: string | null | undefined,
	hasMore: boolean | undefined,
	rankingVersion: string | undefined,
	scopeKey: string,
	reuseExistingSession = false,
	ownership = captureBrowseOwnership(root),
): void {
	const grid = byId(root, 'browseGrid');
	if (!grid) return;
	if (!reuseExistingSession) {
		createBrowsePaginationSession(root, { scopeKey, topics, nextCursor: nextCursor ?? null, hasMore: Boolean(hasMore), rankingVersion });
	}
	const existingCards = Array.from(grid.querySelectorAll<HTMLElement>('.topic-card'));
	const preserveExistingDom = reuseExistingSession
		&& existingCards.length >= topics.length
		&& topics.every((topic, index) => existingCards[index]?.dataset.id === topic.slug);
	let sessionPageState = { nextCursor: nextCursor ?? null, hasMore: Boolean(hasMore) };
	renderPagedInfiniteList({
		container: grid,
		initialPage: { items: topics, nextCursor, hasMore, preserveExistingDom },
		loadPage: async (cursor) => {
			const response = await loadBrowsePage(runtime, filter, cursor, search, sort);
			if (isSnapshotPaginationSort(sort) && cursor && response.error === 'ranking_expired') {
				// This completion may belong to the pre-Topic controller. Do not let a
				// stale 410 clear the trusted return before the progressive-list token
				// has a chance to reject the old continuation.
				if (isCurrentBrowseOwnership(root, runtime, filter, search, sort, scopeKey, ownership)) {
					void restartBrowseAfterExpiredRanking(root, runtime, filter, search, sort, browseViewCacheKey(runtime, filter, search, sort), ownership);
				}
				return { items: [], hasMore: false, nextCursor: null };
			}
			return { items: response.topics, hasMore: response.hasMore, nextCursor: response.nextCursor };
		},
		renderItem: (topic) => buildCard(topic),
		skeletonClassName: 'topic-card-compact-skeleton',
		skeletonCount: 12,
		endMessage: "You've reached the end of the topic catalog.",
		onItemsChange: (allTopics) => {
			(root as unknown as { __browseTopicsCache?: PublicTopic[] }).__browseTopicsCache = allTopics;
			updateBrowsePaginationSession(root, { topics: allTopics, ...sessionPageState, rankingVersion });
			if (!reuseExistingSession) syncCurrentBrowseHistoryEntry(root, runtime, filter, search, sort);
		},
		onPage: (page) => { sessionPageState = { nextCursor: page.nextCursor ?? null, hasMore: Boolean(page.hasMore) }; },
	});
}

type BrowseRequestOwnership = { requestGeneration: number; navigationEpoch: number };

function captureBrowseOwnership(root: Root): BrowseRequestOwnership {
	const holder = root as unknown as {
		__browseRequestGeneration?: number;
		__browseNavigationEpoch?: number;
		__browseRuntime?: PrototypeRuntime;
		__browseFilter?: string;
		__browseSort?: BrowseSort;
	};
	const ownership = { requestGeneration: holder.__browseRequestGeneration ?? 0, navigationEpoch: holder.__browseNavigationEpoch ?? 0 };
	// This is defensive only: normal mount sites already set these values. It
	// prevents an uninitialized holder from making an otherwise-current first
	// pagination stream look stale.
	holder.__browseRequestGeneration = ownership.requestGeneration;
	holder.__browseNavigationEpoch = ownership.navigationEpoch;
	return ownership;
}

function isCurrentBrowseOwnership(
	root: Root,
	runtime: PrototypeRuntime,
	filter: string,
	search: string,
	sort: BrowseSort,
	scopeKey: string,
	ownership: BrowseRequestOwnership,
): boolean {
	const holder = root as unknown as {
		__browseRequestGeneration?: number;
		__browseNavigationEpoch?: number;
		__browseRuntime?: PrototypeRuntime;
		__browseFilter?: string;
		__browseSort?: BrowseSort;
	};
	if (holder.__browseRequestGeneration !== ownership.requestGeneration || holder.__browseNavigationEpoch !== ownership.navigationEpoch) return false;
	if (holder.__browseRuntime !== runtime || holder.__browseFilter !== filter || currentBrowseSearch(root) !== search) return false;
	if (canonicalBrowseSort(holder.__browseSort) !== sort || browsePaginationScopeKey(holder.__browseRuntime, filter, search, sort) !== scopeKey) return false;
	const browseView = root.querySelector<HTMLElement>('#view-home-browse');
	// Lightweight mounts (tests and isolated embeds) intentionally omit the
	// view shell; they still own their current pagination stream.
	return !browseView || browseView.classList.contains('active');
}

async function restartBrowseAfterExpiredRanking(
	root: Root,
	runtime: PrototypeRuntime,
	filter: string,
	search: string,
	sort: BrowseSort,
	pendingCacheKey: string,
	ownership: BrowseRequestOwnership,
): Promise<void> {
	const holder = root as unknown as {
		__browseRequestGeneration?: number;
		__browseNavigationEpoch?: number;
		__browseRenderedKey?: string;
		__browseRuntime?: PrototypeRuntime;
		__browseFilter?: string;
		__browseSort?: BrowseSort;
	};
	const scopeKey = browsePaginationScopeKey(runtime, filter, search, sort);
	// Validate before *every* restart ownership side effect. In particular do
	// not grab a new generation or clear the grid for a continuation that
	// completed after Topic departure, a filter change, or a viewer switch.
	if (!isCurrentBrowseOwnership(root, runtime, filter, search, sort, scopeKey, ownership)) return;
	const generation = (holder.__browseRequestGeneration ?? 0) + 1;
	holder.__browseRequestGeneration = generation;
	holder.__browseRenderedKey = undefined;
	const restartOwnership = { requestGeneration: generation, navigationEpoch: holder.__browseNavigationEpoch ?? 0 };
	const grid = byId(root, 'browseGrid');
	if (!grid) return;
	clearPagedInfiniteList(grid);
	byId(root, 'browseEmpty')?.replaceChildren();
	discardActiveBrowsePaginationSession(root);
	deleteBrowseViewCache(pendingCacheKey);
	const response = await loadBrowsePage(runtime, filter, null, search, sort);
	if (!isCurrentBrowseOwnership(root, runtime, filter, search, sort, scopeKey, restartOwnership)) return;
	if (response.error) {
		showBrowseError(root, browseUnavailableKind(response.error));
		return;
	}
	const rankingVersion = (response as BrowsePage & { rankingVersion?: string }).rankingVersion;
	writeBrowseViewCache(browseViewCacheKey(runtime, filter, search, sort, rankingVersion), response.topics, rankingVersion);
	mountBrowsePagination(root, runtime, filter, search, sort, response.topics, response.nextCursor, response.hasMore, rankingVersion, scopeKey, false, restartOwnership);
	window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
	showToast({ type: 'info', message: 'Content updated. Showing the latest topics.' });
}

function updateBrowseVisitorCatalogCta(root: Root, filter: string, search: string, totalTopics: number): void {
	if (filter === 'all' && search === '') applyVisitorCatalogCta(root, 'browse', totalTopics);
}

function isCurrentBrowseRequest(
	root: Root,
	runtime: PrototypeRuntime,
	holder: { __browseFilter?: string; __browseRequestGeneration?: number; __browseNavigationEpoch?: number; __browseSort?: BrowseSort },
	generation: number,
	navigationEpoch: number,
	cacheKey: string,
	filter: string,
	search: string,
	sort: BrowseSort,
): boolean {
	if (holder.__browseRequestGeneration !== generation || holder.__browseNavigationEpoch !== navigationEpoch) return false;
	if (holder.__browseFilter !== filter || currentBrowseSearch(root) !== search) return false;
	if (canonicalBrowseSort(holder.__browseSort) !== sort || browseViewCacheKey(runtime, filter, search, sort) !== cacheKey) return false;
	const browseView = root.querySelector<HTMLElement>('#view-home-browse');
	return !browseView || browseView.classList.contains('active');
}


export function applyBrowseGridFilter(root: Root, filter: string, _opts: { immediate?: boolean } = {}): void {
  const holder = root as unknown as {
    __browseFilter?: string;
    __browseRuntime?: PrototypeRuntime;
		__browseSort?: BrowseSort;
  };

  // Normalize filter
  const normalized = filter.trim().toLowerCase().replace(/[\s_]+/g, "-");
  holder.__browseFilter = normalized;
  highlightActivePill(root, normalized);
	syncBrowseIndustryMenu(root, normalized);

  const runtime = holder.__browseRuntime;
  if (runtime) {
		const sort = canonicalBrowseSort(holder.__browseSort);
		holder.__browseSort = sort;
		syncBrowseSortControls(root, normalized, sort);
		writeBrowseHistory(root, runtime, normalized, sort, currentBrowseSearch(root));
		syncCurrentBrowseHistoryEntry(root, runtime, normalized, currentBrowseSearch(root), sort);
		void fetchAndRenderBrowse(root, runtime, normalized, currentBrowseSearch(root), sort);
  }
}

/** Changes only ordering. Category/search stay intact and pagination restarts at page one. */
export function setBrowseSort(root: Root, sort: BrowseSort): void {
	const holder = root as unknown as { __browseFilter?: string; __browseRuntime?: PrototypeRuntime; __browseSort?: BrowseSort };
	const runtime = holder.__browseRuntime;
	if (!runtime) return;
	const normalized = canonicalBrowseSort(sort);
	const filter = holder.__browseFilter ?? 'all';
	holder.__browseSort = normalized;
	syncBrowseSortControls(root, filter, normalized);
	writeBrowseHistory(root, runtime, filter, normalized, currentBrowseSearch(root));
	syncCurrentBrowseHistoryEntry(root, runtime, filter, currentBrowseSearch(root), normalized);
	void fetchAndRenderBrowse(root, runtime, filter, currentBrowseSearch(root), normalized);
}

function readBrowseUrlIntent(): { filter?: string; sort?: BrowseSort; search: string; hasSearchParam: boolean; hasExplicitIntent: boolean } {
	if (typeof window === 'undefined' || window.location.pathname.replace(/\/+$/u, '') !== '/browse') return { search: '', hasSearchParam: false, hasExplicitIntent: false };
	const params = new URLSearchParams(window.location.search);
	const category = params.get('category')?.trim().toLowerCase();
	const filter = category && /^[a-z0-9-]+$/u.test(category) ? category : 'all';
	const hasSearchParam = params.has('q');
	return { filter, sort: canonicalBrowseSort(params.get('sort')), search: normalizeBrowseSearch(params.get('q')), hasSearchParam, hasExplicitIntent: params.has('category') || params.has('sort') || hasSearchParam };
}

function canonicalBrowseSort(sort: unknown): BrowseSort {
	return sort === 'trending' ? 'trending' : DEFAULT_BROWSE_SORT;
}

function canonicalizeBrowseUrl(filter: string, sort: BrowseSort, search: string): boolean {
	if (typeof window === 'undefined' || !window.history || window.location.pathname.replace(/\/+$/u, '') !== '/browse') return false;
	// The unfiltered catalog is the canonical directory. Do not turn a bare
	// first visit into a parameter variant merely to spell out its defaults.
	if (filter === 'all' && sort === DEFAULT_BROWSE_SORT && search === '' && window.location.search === '') return false;
	const target = browseUrl(filter, sort, search);
	const currentState = window.history.state && typeof window.history.state === 'object' && !Array.isArray(window.history.state)
		? window.history.state as Record<string, unknown>
		: {};
	const snapshot = currentState.browseSnapshot;
	const snapshotNeedsCanonicalization = snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot)
		&& (snapshot as Record<string, unknown>).sort !== sort;
	if (`${window.location.pathname}${window.location.search}` !== target || snapshotNeedsCanonicalization) {
		window.history.replaceState(snapshotNeedsCanonicalization ? { ...currentState, browseSnapshot: { ...(snapshot as Record<string, unknown>), sort } } : window.history.state, '', target);
	}
	return Boolean(snapshotNeedsCanonicalization);
}

function writeBrowseHistory(root: Root, runtime: PrototypeRuntime, filter: string, sort: BrowseSort, search: string): void {
	if (typeof window === 'undefined' || !window.history) return;
	const target = browseUrl(filter, sort, search);
	if (`${window.location.pathname}${window.location.search}` === target) return;
	const previous = readBrowseUrlIntent();
	// Same-view Browse transitions are real history entries. With manual
	// scroll restoration, their viewport cannot inherit the old entry's dock
	// snapshot: persist A before leaving it, then stamp B with the actual
	// current viewport/Dock state at creation. Filter, sort and committed
	// search all share this one path.
	syncCurrentBrowseHistoryEntry(root, runtime, previous.filter ?? 'all', previous.search, canonicalBrowseSort(previous.sort));
	persistMobileBottomDockHistory(root);
	const dockSnapshot = mobileBottomDockHistorySnapshot(root);
	window.history.pushState({
		...createBrowseHistoryEntryState(window.history.state),
		...(dockSnapshot ? { mobileBottomDock: dockSnapshot } : {}),
	}, '', target);
}

function browseUrl(filter: string, sort: BrowseSort, search: string): string {
	const params = new URLSearchParams({ category: filter, sort });
	if (search) params.set('q', search);
	return `/browse?${params.toString()}`;
}

function syncBrowseSortControls(root: Root, filter: string, sort: BrowseSort): void {
	const exploreControls = byId(root, 'browseExploreControls');
	const quickFilter = isQuickBrowseFilter(filter);
	if (quickFilter) closeBrowseIndustryMenu(root, { restoreFocus: false });
	exploreControls?.toggleAttribute('hidden', quickFilter);
	root.querySelectorAll<HTMLElement>('[data-browse-sort]').forEach((button) => {
		const buttonSort = button.dataset.browseSort as BrowseSort | undefined;
		button.classList.toggle('active', buttonSort === sort);
		button.setAttribute('aria-pressed', String(buttonSort === sort));
	});
	const search = byId(root, 'browseSearchInput') as HTMLInputElement | null;
	search?.setAttribute('placeholder', 'Search topics, keywords...');
	search?.setAttribute('aria-label', 'Search topics, keywords');
}

function isSnapshotPaginationSort(sort: BrowseSort): boolean {
	return sort === 'trending' || sort === 'newest';
}

function browseUnavailableKind(error: BrowseLoadError | undefined): 'trending_unavailable' | 'newest_unavailable' | undefined {
	return error === 'trending_unavailable' || error === 'newest_unavailable' ? error : undefined;
}

function isQuickBrowseFilter(filter: string): boolean {
	return filter === 'recent' || filter === 'recommended' || filter === 'favorites';
}

function highlightActivePill(root: Root, activeFilter: string): void {
	const viewButtons = Array.from(root.querySelectorAll<HTMLElement>('[data-browse-view]'));
	if (viewButtons.length === 0) {
		const row = byId(root, 'browseFilters');
		row?.querySelectorAll('.browse-filter-group--quick .category-pill, .browse-filter-group--categories .category-pill').forEach((node) => node.classList.remove('active'));
		const activeButton = Array.from(root.querySelectorAll<HTMLElement>('#browseFilters .category-pill'))
			.find((button) => (button.dataset.protoClick ?? '').includes(`filterBrowse('${activeFilter}'`));
		activeButton?.classList.add('active');
		return;
	}

	const quickFilter = isQuickBrowseFilter(activeFilter);
	viewButtons.forEach((button) => {
		const active = quickFilter ? button.dataset.browseView === activeFilter : button.dataset.browseView === 'explore';
		button.classList.toggle('active', active);
		if (active) button.setAttribute('aria-current', 'page');
		else button.removeAttribute('aria-current');
	});
	root.querySelectorAll<HTMLElement>('[data-browse-category]').forEach((button) => {
		const active = !quickFilter && button.dataset.browseCategory === activeFilter;
		button.classList.toggle('active', active);
		if (button.getAttribute('role') === 'option') button.setAttribute('aria-selected', String(active));
		else button.setAttribute('aria-pressed', String(active));
	});
}

function showBrowseEmpty(root: Root, activeFilter: string, search: string, sort: BrowseSort = DEFAULT_BROWSE_SORT): void {
  const empty = byId(root, "browseEmpty");
  if (!empty) return;
  // The empty catalog has no grid cards, but the collapsed .topic-grid keeps
  // its margin-bottom (var(--space-48)), leaving a large void between the
  // capsule toolbar and the empty state. Hiding the grid wrapper removes it;
  // fetchAndRenderBrowse restores the wrapper on the next load.
  const grid = byId(root, "browseGrid");
  const wrapper = grid?.parentElement;
  if (wrapper) wrapper.style.display = 'none';
  if (search) {
    empty.innerHTML =
      renderEmptyStateMarkup({ icon: 'book', title: `No topics match “${escapeHtml(search)}”`, subtitle: 'Try a different search, or return to the full catalog.', extraClass: 'browse-empty-state' }) +
      `<div style="display:flex;justify-content:center;gap:8px;margin-top:8px;">` +
      `<button class="btn btn-outline btn-sm" data-proto-click="clearBrowseSearch()">Clear search</button>` +
      `<button class="btn btn-primary btn-sm" data-proto-click="navigateToBrowse('all')">Browse all topics</button></div>`;
    empty.style.display = "";
    return;
  }
  // Bug 2 fix: if the active filter is a category slug (not one of
  // the 4 named capsules), use the "category" empty state instead of
  // falling through to "all" — which says "No topics yet" and makes
  // users think the whole site is empty.
	const namedCapsules = new Set(["recent", "recommended", "favorites", "all"]);
	const key = activeFilter === 'all' && sort === 'newest' ? 'newest' : activeFilter === 'all' && sort === 'trending' ? 'trending' : namedCapsules.has(activeFilter) ? activeFilter : "category";
  const message = BROWSE_EMPTY_MESSAGES[key] ?? BROWSE_EMPTY_MESSAGES.all;
  empty.innerHTML =
    renderEmptyStateMarkup({ ...message, extraClass: 'browse-empty-state' }) +
    (activeFilter === "all" ? "" : `<div style="display:flex;justify-content:center;margin-top:8px;"><button class="btn btn-outline btn-sm" data-proto-click="navigateToBrowse('all')">Browse all topics</button></div>`);
  empty.style.display = "";
}

function showBrowseError(root: Root, kind?: 'trending_unavailable' | 'newest_unavailable'): void {
  const empty = byId(root, "browseEmpty");
  if (!empty) return;
  empty.innerHTML =
		renderEmptyStateMarkup({
		icon: 'inbox',
		title: kind === 'trending_unavailable' ? "Couldn't load Trending topics" : kind === 'newest_unavailable' ? "Couldn't load New topics" : "Couldn't load topics",
		subtitle: 'A network or server error occurred. Please try again.',
		extraClass: 'browse-empty-state browse-empty-state--error',
	}) +
    `<div style="display:flex;justify-content:center;gap:8px;margin-top:14px;">` +
		`<button class="btn btn-primary btn-sm" data-proto-click="refreshBrowse()">Retry</button>` +
		(kind === 'trending_unavailable' ? `<button class="btn btn-outline btn-sm" data-proto-click="setBrowseSort('newest',this)">View New</button>` : kind === 'newest_unavailable' ? `<button class="btn btn-outline btn-sm" data-proto-click="setBrowseSort('trending',this)">View Trending</button>` : '') +
		`</div>`;
  empty.style.display = "";
}


function buildCard(t: PublicTopic): HTMLElement {
  return buildTopicCard(
    {
			slug: t.slug, title: t.title, source: t.source, coverBadge: t.coverBadge, subtitle: t.subtitle, description: t.description,
			category: t.category, classification: t.classification, coverImageUrl: t.coverImageUrl, coverGradient: t.coverGradient,
      coverIcon: t.coverIcon, chapterCount: t.chapterCount, audioMinutes: t.audioMinutes,
      subscribers: t.subscribers, openCount: t.openCount, favoriteCount: t.favoriteCount,
      dynamicScore: t.dynamicScore, recentUpdatedAt: t.recentUpdatedAt ?? null,
      favoritedAt: t.favoritedAt ?? null, createdAt: t.createdAt ?? "",
      isNewThisWeek: t.isNewThisWeek, isRecent: t.isRecent,
      isFavorite: t.isFavorite, isRecommended: t.isRecommended,
      voteValue: t.voteValue,
    },
    { showSubtitle: true, variant: 'mobile-compact' },
  );
}

function wireBrowseSearch(root: Root): void {
  const input = byId(root, "browseSearchInput") as HTMLInputElement | null;
  if (!input || input.dataset.bound === "true") return;
  input.dataset.bound = "true";
  // Search is button-triggered (and Enter key) — NOT live debounce.
  // Live keystroke handlers risk hammering the backend with one
  // request per typed character. Commit sends a fresh server request
  // via fetchAndRenderBrowse so pagination reflects the full result set.
  input.addEventListener("keydown", (event) => {
    if (shouldCommitTextInputOnEnter(event)) {
      event.preventDefault();
      if (typeof input.blur === 'function') input.blur();
      runBrowseSearchInternal(root);
    }
  });
  input.addEventListener("input", () => {
    // Toggle the clear button visibility but DO NOT re-render
    // on every keystroke — the user must press the search button
    // or Enter to commit.
    toggleBrowseClearButton(root);
  });
}

function wireBrowseHistoryScroll(root: Root): void {
	const holder = root as unknown as {
		__browseHistoryScrollBound?: boolean;
		__browseHistoryScrollFrame?: number;
		__browseRuntime?: PrototypeRuntime;
		__browseFilter?: string;
		__browseSort?: BrowseSort;
	};
	if (holder.__browseHistoryScrollBound || typeof window === 'undefined') return;
	holder.__browseHistoryScrollBound = true;
	window.addEventListener('scroll', () => {
		if (holder.__browseHistoryScrollFrame !== undefined) return;
		holder.__browseHistoryScrollFrame = requestAnimationFrame(() => {
			holder.__browseHistoryScrollFrame = undefined;
			if (!root.isConnected || !root.querySelector('#view-home-browse.active')) return;
			const runtime = holder.__browseRuntime;
			if (!runtime) return;
			recordBrowseHistorySnapshot(root, runtime, holder.__browseFilter ?? 'all', currentBrowseSearch(root), canonicalBrowseSort(holder.__browseSort));
		});
	});
}

function toggleBrowseClearButton(root: Root): void {
  const input = byId(root, "browseSearchInput") as HTMLInputElement | null;
  const clearBtn = byId(root, "browseSearchClear") as HTMLButtonElement | null;
  if (!input || !clearBtn) return;
  clearBtn.style.display = input.value.trim() ? "" : "none";
}

function runBrowseSearchInternal(root: Root): void {
  toggleBrowseClearButton(root);
  const filter = (root as unknown as { __browseFilter?: string }).__browseFilter ?? "all";
  const runtime = (root as unknown as { __browseRuntime?: PrototypeRuntime }).__browseRuntime;
  if (!runtime) return;
	const search = currentBrowseSearch(root);
	const sort = canonicalBrowseSort((root as unknown as { __browseSort?: BrowseSort }).__browseSort);
	setBrowseSearch(root, search);
	writeBrowseHistory(root, runtime, filter, sort, search);
	syncCurrentBrowseHistoryEntry(root, runtime, filter, search, sort);
  void fetchAndRenderBrowse(root, runtime, filter, search, sort);
}

/** Action: Browse search button click (data-proto-click="runBrowseSearch()"). */
export function runBrowseSearch(root: Root): void {
  runBrowseSearchInternal(root);
}

/** Action: Browse search clear button (data-proto-click="clearBrowseSearch()"). */
export function clearBrowseSearch(root: Root): void {
  const input = byId(root, "browseSearchInput") as HTMLInputElement | null;
  if (input) input.value = "";
  toggleBrowseClearButton(root);
  runBrowseSearchInternal(root);
}

export function updateBrowseFavorites(root: Root, favoriteTimes: Map<string, string>, _render = true): void {
  // Favorites are now server-side — no local cache to update.
  // A re-fetch will happen naturally when the user clicks the Favorites capsule.
  void favoriteTimes;
}
